<template>
  <div id="faz-mapbox-scrollytelling" ref="scrollytelling">
    <div class="main__graphic" v-if="config">
      <div :id="'mapboxContainer-' + id" class="mapbox"></div>
      <Transition>
        <div
          v-if="
            config &&
            config.minimap &&
            config.minimap.features &&
            currStep &&
            currStep.location &&
            currStep.location.center &&
            currStep.location.center.length === 2 &&
            currStep.minimap
          "
          class="minimap"
        >
          <LocatorMap
            :geoJson="config.minimap"
            :maxHeight="150"
            :markers="getMinimapMarkers()"
          />
        </div>
      </Transition>
    </div>

    <div v-if="config && config.intro" class="scrollytelling_intro">
      <div>
        <h2 class="kicker">{{ config.intro.kicker }}</h2>
        <h1 class="headline" v-html="config.intro.headline"></h1>
        <div class="authors" v-html="config.intro.authors"></div>
        <div class="date">{{ config.intro.date }}</div>
        <div v-if="config.intro.teaser" class="teaser">
          {{ config.intro.teaser }}
        </div>
      </div>
    </div>

    <Scrollama
      v-if="config && mapLoaded"
      :debug="false"
      @step-enter="onStepEnter"
      @step-exit="onStepExit"
    >
      <div
        v-for="(chapter, index) in config.chapters"
        :key="index"
        class="step"
        :class="chapter.alignment"
        :data-step-no="index"
        :id="chapter.id"
      >
        <div v-if="chapter.description">
          <div class="title" v-if="chapter.title" v-html="chapter.title"></div>
          <div
            class="description"
            v-if="
              chapter.description.length > 1 && chapter.alignment != 'picture'
            "
            v-html="chapter.description"
          ></div>
          <!-- Multiple images (new format) -->
          <div class="image" v-if="chapter.images && chapter.images.length">
            <figure v-for="(img, i) in chapter.images" :key="i">
              <img :src="img.url" />
              <div class="image_caption" v-if="img.caption">
                {{ img.caption }}
                <span class="image_source" v-if="img.source">{{
                  img.source
                }}</span>
              </div>
            </figure>
          </div>

          <!-- Single image (legacy format) -->
          <div class="image" v-else-if="chapter.image">
            <img :src="chapter.image" />
            <div class="image_caption" v-if="chapter.image_caption">
              {{ chapter.image_caption }}
              <span class="image_source" v-if="chapter.image_source">{{
                chapter.image_source
              }}</span>
            </div>
          </div>
          <div class="video" v-if="chapter.video">
            <video
              :src="chapter.video"
              :ref="'video_' + chapter.id"
              webkit-playsinline="true"
              playsinline="true"
              loop="loop"
              muted="muted"
            ></video>
            <div class="image_caption" v-if="chapter.video_caption">
              {{ chapter.video_caption }}
              <span class="image_source" v-if="chapter.video_source">{{
                chapter.video_source
              }}</span>
            </div>
          </div>
          <div
            class="description"
            v-if="
              chapter.description.length > 1 && chapter.alignment == 'picture'
            "
            v-html="chapter.description"
          ></div>
          <div
            v-for="(legend, index) in chapter.legend || []"
            :key="index"
            class="legend-item"
          >
            <div
              v-if="legend.type"
              :class="legend.type"
              :style="{ backgroundColor: legend.color }"
            ></div>
            <span v-if="legend.text">{{ legend.text }}</span>
            <div v-if="legend.image" class="image">
              <img :src="legend.image" />
            </div>
          </div>
        </div>
      </div>
    </Scrollama>
  </div>
</template>

<script>
import "intersection-observer";
import Scrollama from "vue-scrollama";
import maplibregl from "maplibre-gl";
import LocatorMap from "./components/LocatorMap.vue";

import { Protocol } from "pmtiles";
import { cogProtocol } from "@geomatico/maplibre-cog-protocol";

let protocol = new Protocol({ useWorker: true });
maplibregl.addProtocol("pmtiles", protocol.tile);
maplibregl.addProtocol("cog", cogProtocol);

export default {
  name: "App",
  components: {
    Scrollama,
    LocatorMap,
  },
  props: ["id"],
  data() {
    return {
      map: null,
      currStep: null,
      config: null,
      mapLoaded: false,
      width: 500,
      markers: [],
      layerTypes: {
        fill: ["fill-opacity"],
        line: ["line-opacity"],
        circle: ["circle-opacity", "circle-stroke-opacity"],
        symbol: ["icon-opacity", "text-opacity"],
        raster: ["raster-opacity"],
        "fill-extrusion": ["fill-extrusion-opacity"],
        heatmap: ["heatmap-opacity"],
      },
      firstSymbolId: null,
    };
  },
  computed: {
    zoomFactor() {
      return this.width > 600 ? 1 : 0.8;
    },
    isDevelopment() {
      return process.env.NODE_ENV === "development";
    },
    baseUrl() {
      if (this.isDevelopment) {
        return "";
      }
      return document.getElementById("config-" + this.id).dataset.config;
    },
  },
  methods: {
    getMinimapMarkers() {
      console.log("🗺️ getMinimapMarkers called");
      console.log("  currStep:", this.currStep);
      console.log("  currStep.location:", this.currStep?.location);

      if (!this.currStep || !this.currStep.location) {
        console.warn("⚠️ No currStep or location - returning empty markers");
        return [];
      }

      if (
        !this.currStep.location.center ||
        this.currStep.location.center.length < 2
      ) {
        console.error(
          "❌ Invalid location.center:",
          this.currStep.location.center
        );
        return [];
      }

      const markers = [
        {
          long: this.currStep.location.center[0],
          lat: this.currStep.location.center[1],
          label: "",
        },
      ];

      console.log("  ✅ Returning markers:", markers);
      return markers;
    },

    layerExists(layerId) {
      return this.map && this.map.getLayer(layerId) !== undefined;
    },

    sourceExists(sourceId) {
      return this.map && this.map.getSource(sourceId) !== undefined;
    },

    getUrl(path) {
      return this.isDevelopment ? path : this.baseUrl + path;
    },

    getDataUrl(filename) {
      return this.isDevelopment ? "data/" + filename : this.baseUrl + filename;
    },

    initMap() {
      console.log("🚀 initMap called");
      this.width = window.innerWidth;
      console.log("  Window width:", this.width);

      const configUrl = this.getUrl("config.json");
      const styleUrl = this.getUrl("style.json");

      console.log("  config.json url:", configUrl);
      console.log("  style.json url:", styleUrl);

      fetch(configUrl)
        .then((r) => {
          console.log("📥 config.json fetch response received");
          return r.json();
        })
        .then((r) => {
          console.log("✅ config.json parsed:", r);
          this.config = r;
          return r;
        })
        .then(() => {
          console.log("🗺️ Creating map instance");

          // Fallback auf erstes Chapter, wenn kein intro vorhanden
          let location = null;
          if (this.config.intro && this.config.intro.location) {
            location = this.config.intro.location;
            console.log("  Using intro.location");
          } else if (this.config.global && this.config.global.location) {
            location = this.config.global.location;
            console.log("  Using global.location");
          } else {
            location = this.config.chapters[0].location;
            console.log("  Using first chapter location");
          }

          console.log("  Initial location:", location);

          this.map = new maplibregl.Map({
            container: "mapboxContainer-" + this.id,
            style: styleUrl,
            center: location.center,
            zoom: location.zoom * this.zoomFactor,
            pitch: location.pitch || 0,
            bearing: location.bearing || 0,
            scrollZoom: false,
          });

          console.log("  Map instance created, waiting for 'load' event");

          this.map.scrollZoom.disable();
          this.map.boxZoom.disable();
          this.map.doubleClickZoom.disable();
          this.map.touchZoomRotate.disable();
          this.map.keyboard.disable();

          this.map.on("load", () => {
            console.log("🎉 Map 'load' event fired");

            const layers = this.map.getStyle().layers;
            console.log("  Total layers loaded:", layers.length);

            // Find first symbol layer
            for (let i = 0; i < layers.length; i++) {
              if (layers[i].type === "symbol") {
                this.firstSymbolId = layers[i].id;
                console.log("  First symbol layer:", this.firstSymbolId);
                break;
              }
            }

            // Intro-Setup nur wenn vorhanden
            if (this.config.intro) {
              console.log("  Setting up intro");
              this.setupIntro();
            }

            this.mapLoaded = true;
            console.log(
              "✅ mapLoaded set to true - Scrollama will now initialize"
            );
          });
        })
        .catch((error) => {
          console.error("❌ Error in initMap:", error);
        });
    },

    setupIntro() {
      console.log("🎬 setupIntro called");
      const intro = this.config.intro;

      if (intro.bounds) {
        console.log("  Fitting bounds:", intro.bounds);
        this.map.fitBounds(intro.bounds);
      }

      if (intro.choropleth) {
        console.log("  Adding choropleth layer");
        this.addChoroplethLayer(intro.choropleth, intro.choropleth.id);
      }

      if (intro.area) {
        console.log("  Adding area layer");
        this.addAreaLayer(intro.area, intro.area.id);
      }
    },

    addChoroplethLayer(choropleth, layerId) {
      if (this.layerExists(layerId)) {
        console.log(`Layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.isDevelopment ? choropleth.geojson : choropleth.geojson_live,
      });

      this.map.addLayer({
        id: layerId,
        source: layerId,
        type: "fill",
        paint: {
          "fill-color": "#c60000",
          "fill-opacity": 0.5,
          "fill-outline-color": "#c60000",
        },
      });
    },

    addAreaLayer(area, layerId) {
      if (this.layerExists(layerId)) {
        console.log(`Layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.getDataUrl(area.geojson),
      });

      this.map.addLayer({
        id: layerId,
        source: layerId,
        type: "fill",
        paint: {
          "fill-color": area.fill || "#c60000",
          "fill-opacity": area.opacity,
          "fill-outline-color": area.fill || "#c60000",
        },
      });
    },

    onStepEnter({ element, index }) {
      console.log("👣 onStepEnter called", { element, index });
      console.log("  Current mapLoaded state:", this.mapLoaded);
      console.log("  Current config state:", !!this.config);

      const chapter = this.config.chapters[index];
      console.log("  Chapter data:", chapter);

      this.currStep = chapter;
      console.log("  currStep updated to:", this.currStep);

      element.classList.add("active");

      // Video handling
      if (chapter.video) {
        const video = this.$refs["video_" + element.id]?.[0];
        if (video) {
          video.click();
          video.play();
        }
      }

      // Logic to handle bounds or location
      const defaultLocationZoom = 16;
      const defaultLocationPitch = 0;
      const defaultLocationBearing = 0;

      if (chapter.bounds && chapter.bounds.length > 0) {
        console.log("  Flying to bounds:", chapter.bounds);
        this.map.fitBounds(chapter.bounds, {
          padding: chapter.bounds_padding || 0,
          speed: 1,
          curve: 1,
          pitch: chapter.location?.pitch || defaultLocationPitch,
          bearing: chapter.location?.bearing || defaultLocationBearing || 0,
        });
      } else if (chapter.location) {
        console.log("  Flying to location:", chapter.location);
        this.map.flyTo({
          center: chapter.location.center,
          zoom: chapter.location?.zoom || defaultLocationZoom,
          pitch: chapter.location?.pitch || defaultLocationPitch,
          bearing: chapter.location?.bearing || defaultLocationBearing || 0,
          speed: 1,
          curve: 1,
        });
      } else {
        console.error(
          "❌ No bounds or location data available for the chapter."
        );
      }

      // Markers
      if (chapter.markers) {
        this.addMarkers(chapter.markers);
      }

      if (chapter.marker) {
        this.addSingleMarker(chapter.marker, chapter.location.center);
      }

      // Layer opacity changes
      if (chapter.onChapterEnter && chapter.onChapterEnter.length > 0) {
        chapter.onChapterEnter.forEach(this.setLayerOpacity);
      }

      // Add various layer types
      if (chapter.overlay) {
        this.addOverlayLayer(chapter);
      }

      if (chapter.area) {
        this.addChapterAreaLayer(chapter);
      }

      if (chapter.choropleth) {
        this.addChapterChoroplethLayer(chapter);
      }

      if (chapter.line) {
        this.addLineLayer(chapter);
      }

      if (chapter.points) {
        this.addPointsLayer(chapter);
      }

      if (chapter.cog) {
        this.addCogLayer(chapter);
      }

      // Country highlighting
      this.handleCountryHighlighting(chapter);
    },

    onStepExit({ element, index }) {
      const chapter = this.config.chapters[index];

      element.classList.remove("active");

      if (chapter.onChapterExit && chapter.onChapterExit.length > 0) {
        chapter.onChapterExit.forEach(this.setLayerOpacity);
      }

      if (chapter.markers) {
        this.removeMarkers();
      }

      if (chapter.video) {
        const video = this.$refs["video_" + element.id]?.[0];
        if (video) {
          video.click();
          video.pause();
        }
      }
    },

    addMarkers(markers) {
      markers.forEach((marker) => {
        const el = document.createElement("div");
        el.className = "marker " + marker.type;
        el.innerText = "My Label";

        if (marker.type !== "label" && marker.type !== "label-left") {
          el.innerHTML = marker.layer ?? "";
        } else {
          el.dataset.label = marker.layer ?? "";
          el.title = marker.layer ?? "";
        }

        const m = new maplibregl.Marker({ element: el })
          .setLngLat(marker.location)
          .addTo(this.map);
        this.markers.push(m);
      });
    },

    addSingleMarker(markerHtml, location) {
      const el = document.createElement("div");
      el.innerHTML = markerHtml;
      el.className = "marker";
      new maplibregl.Marker(el).setLngLat(location).addTo(this.map);
    },

    removeMarkers() {
      this.markers.forEach((marker) => marker.remove());
      this.markers = [];
    },

    addOverlayLayer(chapter) {
      const layerId = chapter.overlay.id;

      if (this.layerExists(layerId)) {
        console.log(`Overlay layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "image",
        url: this.getDataUrl(chapter.overlay.url),
        coordinates: chapter.overlay.coordinates,
      });

      const opacity =
        chapter.onChapterEnter?.find((x) => x.layer === layerId)?.opacity || 1;

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "raster",
          paint: {
            "raster-opacity": opacity,
          },
        },
        this.firstSymbolId
      );
    },

    addChapterAreaLayer(chapter) {
      const layerId = chapter.area.id;

      if (this.layerExists(layerId)) {
        console.log(`Area layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.getDataUrl(chapter.area.geojson),
      });

      const opacity =
        chapter.onChapterEnter?.find((x) => x.layer === layerId)?.opacity ||
        0.5;

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "fill",
          paint: {
            "fill-color": chapter.area.fill || "#c60000",
            "fill-opacity": opacity,
            "fill-outline-color": chapter.area.fill || "#c60000",
          },
        },
        this.firstSymbolId
      );
    },

    addChapterChoroplethLayer(chapter) {
      const layerId = chapter.choropleth.id;

      if (this.layerExists(layerId)) {
        console.log(`Choropleth layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.getDataUrl(chapter.choropleth.geojson),
      });

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "fill",
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", chapter.choropleth.property],
              ...chapter.choropleth.domain.flatMap((d, i) => [
                d,
                chapter.choropleth.range[i],
              ]),
            ],
            "fill-opacity": 0,
            "fill-outline-color": "#000",
          },
        },
        this.firstSymbolId
      );
    },

    addLineLayer(chapter) {
      const layerId = chapter.line.id;

      if (this.layerExists(layerId)) {
        console.log(`Line layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.getDataUrl(chapter.line.geojson),
      });

      const paint = {
        "line-color": chapter.line.line_color,
        "line-width": chapter.line.line_width || 4,
      };

      if (chapter.line.line_dasharray) {
        paint["line-dasharray"] = chapter.line.line_dasharray;
      }

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "line",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: paint,
        },
        this.firstSymbolId
      );
    },

    addPointsLayer(chapter) {
      const layerId = chapter.points.id;

      if (this.layerExists(layerId)) {
        console.log(`Points layer ${layerId} already exists`);
        return;
      }

      this.map.addSource(layerId, {
        type: "geojson",
        data: this.getDataUrl(chapter.points.geojson),
      });

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "circle",
          paint: {
            "circle-color": chapter.points.color,
            "circle-radius": chapter.points.radius || 3,
          },
        },
        this.firstSymbolId
      );
    },

    addCogLayer(chapter) {
      const layerId = chapter.cog.id;

      if (this.layerExists(layerId)) {
        console.log(`COG layer ${layerId} already exists`);
        return;
      }

      const cogUrl = this.isDevelopment
        ? "cog://http://127.0.0.1:3000/" + chapter.cog.tif
        : "cog://" + this.baseUrl + chapter.cog.tif;

      console.log("COG URL:", cogUrl);

      this.map.addSource(layerId, {
        type: "raster",
        url: cogUrl,
      });

      this.map.addLayer(
        {
          id: layerId,
          source: layerId,
          type: "raster",
        },
        this.firstSymbolId
      );
    },

    handleCountryHighlighting(chapter) {
      if (
        chapter.highlight_countries &&
        chapter.highlight_countries.length > 0
      ) {
        if (
          this.layerExists("highlighted-countries") &&
          this.layerExists("countries-mask")
        ) {
          this.map.setFilter("highlighted-countries", [
            "in",
            ["get", "iso_a3"],
            ["literal", chapter.highlight_countries],
          ]);
          this.map.setLayoutProperty(
            "highlighted-countries",
            "visibility",
            "visible"
          );

          this.map.setFilter("countries-mask", [
            "!",
            ["in", ["get", "iso_a3"], ["literal", chapter.highlight_countries]],
          ]);
          this.map.setLayoutProperty("countries-mask", "visibility", "visible");
        }
      } else {
        if (this.layerExists("highlighted-countries")) {
          this.map.setLayoutProperty(
            "highlighted-countries",
            "visibility",
            "none"
          );
        }
        if (this.layerExists("countries-mask")) {
          this.map.setLayoutProperty("countries-mask", "visibility", "none");
        }
      }
    },

    getLayerPaintType(layer) {
      if (!this.layerExists(layer)) {
        console.warn(`Layer ${layer} does not exist`);
        return null;
      }

      const layerType = this.map.getLayer(layer).type;
      return this.layerTypes[layerType];
    },

    setLayerOpacity(layer) {
      const paintProps = this.getLayerPaintType(layer.layer);

      if (!paintProps) {
        console.warn(
          `Cannot set opacity for layer ${layer.layer} - layer not found or type unknown`
        );
        return;
      }

      paintProps.forEach((prop) => {
        const options = {};
        if (layer.duration) {
          const transitionProp = prop + "-transition";
          options.duration = layer.duration;
          this.map.setPaintProperty(layer.layer, transitionProp, options);
        }
        this.map.setPaintProperty(layer.layer, prop, layer.opacity, options);
      });

      if (layer.color) {
        this.map.setPaintProperty(layer.layer, "fill-color", layer.color);
      }

      if (layer.visibility) {
        this.map.setLayoutProperty(layer.layer, "visibility", layer.visibility);
      }
    },
  },
  mounted() {
    console.log("🎯 Component mounted");
    console.log("  Props id:", this.id);

    let init = true;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        console.log("👀 IntersectionObserver triggered", {
          isIntersecting: entry.isIntersecting,
          init: init,
        });

        if (entry.isIntersecting && !this.elementIsVisible) {
          if (init) {
            console.log("  🚀 Initializing map for the first time");
            this.initMap();
          }
          init = false;
        }
      });
    });
    observer.observe(this.$refs.scrollytelling);
    console.log("  Observer attached to scrollytelling element");
  },
};
</script>

<style lang="scss">
@import "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css";
@import "./assets/style.scss";
</style>
